import React, { useEffect, useState } from 'react'
import type { TabType } from '../components/CheckInCheckOut/CheckInMain'
import { useColorMode, Skeleton } from '@chakra-ui/react'
import { formatDateDDMMYYYY } from '../utils/dateUtils'
import { fetchAllLeaveEntriesForUsers, updateLeaveEntryStatus, bulkUpdateLeaveEntries, fetchLeaveEntries, formatNumDays, describeDayPortions } from '../utils/leaveUtils'
import type { LeaveEntry } from '../utils/leaveUtils'
import { invalidateCheckInCache } from './CheckInAlert'

type User = {
  id: string
  firstName?: string
  lastName?: string
  role?: string
  permissions?: "Admin" | "Editor" | "Viewer"
  approvalStatus?: "Yes" | "No"
}

interface LeaveApprovalPageProps {
  organizationId: string
  currentUserId: string
  users: User[]
  onBack?: () => void
  setActiveTab?: React.Dispatch<React.SetStateAction<TabType>>
  activeSubTab?: "timesheets" | "leaves" | "wfh" | "noncheckin"
  setActiveSubTab?: React.Dispatch<React.SetStateAction<"timesheets" | "leaves" | "wfh" | "noncheckin">>
}

export default function LeaveApprovalPage({ organizationId, currentUserId, users, onBack, setActiveTab, activeSubTab, setActiveSubTab }: LeaveApprovalPageProps) {
  const [loading, setLoading] = useState(true)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [entriesByUser, setEntriesByUser] = useState<Record<string, (LeaveEntry & { id: string })[]>>({})
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [isUserPickerOpen, setIsUserPickerOpen] = useState(false)
  const [userQuery, setUserQuery] = useState('')
  const [userPickerPlacement, setUserPickerPlacement] = useState<'top' | 'bottom'>('bottom')
  const userPickerRef = React.useRef<HTMLDivElement | null>(null)
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => Promise<void> | void;
  } | null>(null)

  const me = users.find(u => u.id === currentUserId)
  const userPermissions = me?.permissions;

  // Admin sees all; managers/approvers see only entries assigned to them
  const isAdmin = !!(me as any)?.isOrgAdmin || userPermissions?.toLowerCase() === "admin" || (me?.role || "").toLowerCase() === "admin";

  useEffect(() => {
    if (!organizationId) return

    const setupListener = async () => {
      try {
        // Fetch all leave entries for all users
        const allEntriesByUser = await fetchAllLeaveEntriesForUsers(organizationId, users.map(u => u.id))

        const byUser: Record<string, (LeaveEntry & { id: string })[]> = {}

        Object.entries(allEntriesByUser).forEach(([uid, entries]) => {
          const userExists = users.some(user => user.id === uid)
          if (!userExists) return

          const sortedEntries = [...entries].sort((a, b) => {
            const dateA = new Date(a.fromDate).getTime();
            const dateB = new Date(b.fromDate).getTime();
            if (dateA !== dateB) return dateB - dateA;
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          })

          sortedEntries.forEach((e, idx) => {
            if ((e.approvalStatus || 'Pending') !== 'Pending') return
            if (uid === currentUserId) return

            const include = isAdmin || (Array.isArray(e.managerIds) && e.managerIds?.includes(currentUserId))
            if (!include) return
            byUser[uid] = byUser[uid] || []
            byUser[uid].push({ ...e, id: e._entryId || String(idx), userId: uid })
          })
        })
        setEntriesByUser(byUser)
        setLoading(false)
      } catch (err) {
        console.error("Error setting up leave listener:", err)
        setLoading(false)
      }
    }

    setupListener()
  }, [organizationId, currentUserId, isAdmin, users])

  // Only show users who have leave requests in the dropdown
  const userOptions = users.filter(user => entriesByUser[user.id] && entriesByUser[user.id].length > 0)
  useEffect(() => {
    if (!selectedUser) setSelectedUser('ALL')
  }, [selectedUser])

  // Close user picker on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-picker]')) {
        setIsUserPickerOpen(false)
      }
    }
    if (isUserPickerOpen) {
      document.addEventListener('mousedown', onClick)
      return () => document.removeEventListener('mousedown', onClick)
    }
  }, [isUserPickerOpen])

  // Decide dropdown placement
  useEffect(() => {
    const recomputePlacement = () => {
      const el = userPickerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const desiredHeight = 240
      if (spaceBelow < desiredHeight && spaceAbove > spaceBelow) {
        setUserPickerPlacement('top')
      } else {
        setUserPickerPlacement('bottom')
      }
    }
    if (isUserPickerOpen) {
      recomputePlacement()
      window.addEventListener('resize', recomputePlacement)
      window.addEventListener('scroll', recomputePlacement, true)
      return () => {
        window.removeEventListener('resize', recomputePlacement)
        window.removeEventListener('scroll', recomputePlacement, true)
      }
    }
  }, [isUserPickerOpen])

  const displayed = selectedUser === 'ALL' ? Object.values(entriesByUser).flat() : (entriesByUser[selectedUser] || [])

  const updateStatus = async (userId: string, entryIndex: string, newStatus: 'Approved' | 'Rejected') => {
    const userEntries = entriesByUser[userId] || []
    const entry = userEntries.find(e => e.id === entryIndex)
    if (!entry || !entry._monthKey || !entry._entryId) return

    const key = `${userId}-${entryIndex}`
    setProcessingId(key)
    try {
      await updateLeaveEntryStatus(organizationId, userId, entry, newStatus)
      invalidateCheckInCache()

      // Refresh entries
      const updatedEntries = await fetchLeaveEntries(organizationId, userId)
      const pending = updatedEntries
        .filter(e => (e.approvalStatus || 'Pending') === 'Pending')
        .map((e, idx) => ({ ...e, id: e._entryId || String(idx), userId }))
      setEntriesByUser(prev => ({ ...prev, [userId]: pending }))
    } finally {
      setProcessingId(null)
    }
  }

  // Approve/Reject all pending entries for the selected scope (user or all)
  const updateAllStatus = async (newStatus: 'Approved' | 'Rejected') => {
    const targetUserIds = selectedUser === 'ALL' ? Object.keys(entriesByUser) : [selectedUser]
    await Promise.all(targetUserIds.map(async (uid) => {
      const allEntries = await fetchLeaveEntries(organizationId, uid)
      await bulkUpdateLeaveEntries(organizationId, uid, allEntries, currentUserId, isAdmin, newStatus)
    }))
    invalidateCheckInCache()
    // Refresh
    for (const uid of targetUserIds) {
      const entries = await fetchLeaveEntries(organizationId, uid)
      const pending = entries
        .filter(e => (e.approvalStatus || 'Pending') === 'Pending')
        .map((e, idx) => ({ ...e, id: e._entryId || String(idx), userId: uid }))
      setEntriesByUser(prev => ({ ...prev, [uid]: pending }))
    }
  }

  // Approve/Reject all pending entries for a specific user (group section)
  const updateAllForUser = async (uid: string, newStatus: 'Approved' | 'Rejected') => {
    const allEntries = await fetchLeaveEntries(organizationId, uid)
    await bulkUpdateLeaveEntries(organizationId, uid, allEntries, currentUserId, isAdmin, newStatus)
    invalidateCheckInCache()
    // Refresh
    const entries = await fetchLeaveEntries(organizationId, uid)
    const pending = entries
      .filter(e => (e.approvalStatus || 'Pending') === 'Pending')
      .map((e, idx) => ({ ...e, id: e._entryId || String(idx), userId: uid }))
    setEntriesByUser(prev => ({ ...prev, [uid]: pending }))
  }

  // Confirmation wrappers
  const confirmAndUpdateAll = (newStatus: 'Approved' | 'Rejected') => {
    const scope = selectedUser === 'ALL' ? 'all users' : (() => {
      const u = users.find(uu => uu.id === selectedUser)
      return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || selectedUser : selectedUser
    })()
    const actionWord = newStatus === 'Approved' ? 'approve' : 'reject'
    setConfirmState({
      open: true,
      title: `Confirm ${newStatus}`,
      description: `Are you sure you want to ${actionWord} all pending leave requests for ${scope}?`,
      confirmText: newStatus === 'Approved' ? 'Approve All' : 'Reject All',
      cancelText: 'Cancel',
      onConfirm: async () => { await updateAllStatus(newStatus) }
    })
  }

  const confirmAndUpdateAllForUser = (uid: string, newStatus: 'Approved' | 'Rejected') => {
    const u = users.find(uu => uu.id === uid)
    const name = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || uid : uid
    const actionWord = newStatus === 'Approved' ? 'approve' : 'reject'
    setConfirmState({
      open: true,
      title: `Confirm ${newStatus}`,
      description: `Are you sure you want to ${actionWord} all pending leave requests for ${name}?`,
      confirmText: newStatus === 'Approved' ? 'Approve All' : 'Reject All',
      cancelText: 'Cancel',
      onConfirm: async () => { await updateAllForUser(uid, newStatus) }
    })
  }

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  return (
    <div className="w-full h-full flex flex-col py-0 px-4 min-h-0 text-gray-900">
      <div className={`flex justify-between items-center p-4 min-h-[64px] overflow-visible ${isDark ? 'relative overflow-hidden bg-gradient-to-br from-[#0B0B0F] via-[#1A1C1F] to-[#0B0B0F] ring-1 ring-[#2E2F33] shadow-[inset_0_1px_0_rgba(251,210,130,0.08),inset_0_-1px_0_rgba(251,210,130,0.05),0_10px_28px_rgba(0,0,0,0.45)]' : 'bg-gradient-to-r from-[#FFF7E6] via-[#FAEDD0] to-[#FFF8EA] shadow-[inset_0_-1px_0_0_rgba(229,231,235,0.7),0_2px_8px_rgba(181,145,67,0.08)]'} rounded-md mb-4 border ${isDark ? 'border-[#2E2F33]' : 'border-[#E5E7EB]'} flex-shrink-0`}>
        {isDark && (
          <>
            <span className="pointer-events-none absolute inset-0 [background:radial-gradient(120%_60%_at_50%_120%,rgba(181,145,67,0.18)_0%,rgba(0,0,0,0)_60%)]" />
            <span className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FBD282]/35 to-transparent" />
            <span className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#B59143]/25 to-transparent blur-[2px]" />
            <span className="pointer-events-none absolute -left-10 top-1/2 h-24 w-1/2 -translate-y-1/2 -rotate-6 bg-gradient-to-r from-transparent via-[#FBD282]/14 to-transparent blur-sm" />
          </>
        )}
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-0">
            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Approvals</span>
            <span className={`text-xs ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Review and approve pending leave requests.</span>
          </div>
        </div>
        {/* Tab Switcher — rightmost end of header */}
        {setActiveSubTab && (
          <div
            role="tablist"
            aria-label="Approval type"
            className={`relative grid grid-cols-4 ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white/80 border-gray-200'
              } border rounded-full p-1.5 shadow-sm`}
          >
            <span
              className={`absolute top-1.5 bottom-1.5 rounded-full transition-all duration-300 ${isDark ? 'bg-[#2A3441]' : 'bg-[#FFF7E6] border border-[#E8C16C]'
                } shadow-sm`}
              style={
                activeSubTab === 'timesheets'
                  ? { left: '0.375rem', right: '75%' }
                  : activeSubTab === 'leaves'
                  ? { left: '25%', right: '50%' }
                  : activeSubTab === 'wfh'
                  ? { left: '50%', right: '25%' }
                  : { left: '75%', right: '0.375rem' }
              }
              aria-hidden="true"
            />
            <button
              role="tab"
              aria-selected={activeSubTab === 'timesheets'}
              onClick={() => setActiveSubTab('timesheets')}
              className={`relative z-10 flex items-center justify-center text-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'timesheets'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Timesheets
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'leaves'}
              onClick={() => setActiveSubTab('leaves')}
              className={`relative z-10 flex items-center justify-center text-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'leaves'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Leaves
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'wfh'}
              onClick={() => setActiveSubTab('wfh')}
              className={`relative z-10 flex items-center justify-center text-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'wfh'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Remote Work
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'noncheckin'}
              onClick={() => setActiveSubTab('noncheckin')}
              className={`relative z-10 flex items-center justify-center text-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'noncheckin'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Non Checkins
            </button>
          </div>
        )}
      </div>


      <div className={`max-w-full rounded-md ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] text-white' : 'border-gray-200 bg-white'} border flex-1 min-h-0 flex flex-col`}>
        {loading ? (
          <div className="p-6 flex-1 min-h-0 overflow-y-auto">
            <div className={`rounded-md overflow-hidden border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}>
                    <tr className="h-10">
                      <th className="px-4 py-3 text-left font-semibold"><Skeleton h="16px" w="40px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                      <th className="px-4 py-3 text-left font-semibold"><Skeleton h="16px" w="60px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                      <th className="px-4 py-3 text-left font-semibold"><Skeleton h="16px" w="50px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                      <th className="px-4 py-3 text-left font-semibold"><Skeleton h="16px" w="50px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                      <th className="px-4 py-3 text-center font-semibold"><Skeleton h="16px" w="30px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                      {isAdmin && <th className="px-4 py-3 text-left font-semibold"><Skeleton h="16px" w="60px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>}
                      <th className="px-4 py-3 text-left font-semibold"><Skeleton h="16px" w="50px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? 'divide-y divide-[#2E2F33]' : 'divide-y divide-gray-200'}`}>
                    {[1, 2, 3, 4, 5].map((rowIdx) => (
                      <tr key={rowIdx} className={`h-12 ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'}`}>
                        <td className="px-4 py-3"><Skeleton h="14px" w="60px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                        <td className="px-4 py-3"><Skeleton h="14px" w="150px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                        <td className="px-4 py-3"><Skeleton h="14px" w="80px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                        <td className="px-4 py-3"><Skeleton h="14px" w="80px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                        <td className="px-4 py-3 text-center"><Skeleton h="14px" w="20px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                        {isAdmin && <td className="px-4 py-3"><Skeleton h="14px" w="90px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>}
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Skeleton h="24px" w="55px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} />
                            <Skeleton h="24px" w="50px" borderRadius="sm" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : displayed.length === 0 ? (
          <div className="p-8 flex-1 min-h-0">
            <div className={`flex justify-center items-center h-[40vh] ${isDark ? 'text-white' : 'text-gray-900'}`}>No leave requests available.</div>
          </div>
        ) : (
          <>
            {/* ── Normal action bar ── */}
            <div className="flex items-center justify-between gap-3 px-6 pt-2 mb-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  className={`h-9 px-3 rounded-md text-sm flex items-center gap-1 border transition-colors ${displayed.length === 0 ? 'border-gray-300 text-gray-400 cursor-not-allowed' : 'border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]'}`}
                  onClick={() => confirmAndUpdateAll('Approved')}
                  disabled={displayed.length === 0}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Approve All
                </button>
                <button
                  className={`h-9 px-3 rounded-md text-sm flex items-center gap-1 border transition-colors ${displayed.length === 0 ? 'border-gray-300 text-gray-400 cursor-not-allowed' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                  onClick={() => confirmAndUpdateAll('Rejected')}
                  disabled={displayed.length === 0}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  Reject All
                </button>
              </div>
              {/* User Picker in action bar */}
              <div className="relative flex-shrink-0 w-48" data-user-picker ref={userPickerRef}>
                <button
                  type="button"
                  onClick={() => { setIsUserPickerOpen(o => !o); setUserQuery('') }}
                  className={`w-full h-9 flex items-center gap-2 rounded-md border ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-white' : 'border-gray-300 bg-white text-gray-800'} px-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#B59143] text-left`}
                >
                  <svg className="w-4 h-4 text-gray-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                  <span className={`flex-1 text-sm truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {selectedUser === 'ALL' ? 'All Users' : (() => {
                      const u = users.find(u => u.id === selectedUser)
                      return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.id : selectedUser
                    })()}
                  </span>
                  <svg className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${isUserPickerOpen ? 'rotate-180' : 'rotate-0'}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" /></svg>
                </button>
                {isUserPickerOpen && (
                  <div
                    className={`fixed z-[9999] w-48 rounded-lg border ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-gray-200 bg-white'} shadow-lg`}
                    style={(() => {
                      const btn = userPickerRef.current?.querySelector('button');
                      if (!btn) return { top: 0, left: 0 };
                      const r = btn.getBoundingClientRect();
                      return { top: r.bottom + 4, left: r.left, width: r.width };
                    })()}
                  >
                    <div className={`sticky top-0 ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-100'} border-b p-2`}>
                      <input
                        autoFocus
                        value={userQuery}
                        onChange={e => setUserQuery(e.target.value)}
                        placeholder="Search user..."
                        className={`w-full rounded-md border ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900'} px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#B59143]`}
                      />
                    </div>
                    <div className="max-h-60 overflow-auto">
                      <button
                        className={`w-full text-left px-3 py-2 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'} ${selectedUser === 'ALL' ? 'bg-[#FFF7E6] !text-black cursor-default' : 'hover:bg-[#FFF7E6] hover:text-gray-900'}`}
                        aria-selected={selectedUser === 'ALL'}
                        onClick={() => { setSelectedUser('ALL'); setIsUserPickerOpen(false) }}
                      >
                        All Users
                      </button>
                      {userOptions.filter(u => `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(userQuery.toLowerCase())).map(u => (
                        <button
                          key={u.id}
                          className={`w-full text-left px-3 py-2 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'} ${selectedUser === u.id ? 'bg-[#FFF7E6] !text-black cursor-default' : 'hover:bg-[#FFF7E6] hover:text-gray-900'}`}
                          aria-selected={selectedUser === u.id}
                          onClick={() => { setSelectedUser(u.id); setIsUserPickerOpen(false) }}
                        >
                          {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.id}
                        </button>
                      ))}
                      {userOptions.filter(u => `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(userQuery.toLowerCase())).length === 0 && (
                        <div className={`px-3 py-2 text-sm ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>No users found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {selectedUser === 'ALL' ? (
              <div className="px-6 pb-2 space-y-6">
                {Object.keys(entriesByUser).map(uid => {
                  const user = users.find(u => u.id === uid)
                  const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || uid
                  const items = entriesByUser[uid] || []
                  if (items.length === 0) return null
                  return (
                    <div key={uid} className={`rounded-md overflow-hidden border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
                      <div className={`flex items-center justify-between px-4 py-2 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                        <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{name}</div>
                        <div className="flex items-center gap-2">
                          <button
                            className="h-8 px-3 rounded-md text-xs flex items-center gap-1 border border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]"
                            onClick={() => confirmAndUpdateAllForUser(uid, 'Approved')}
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            Approve All
                          </button>
                          <button
                            className="h-8 px-3 rounded-md text-xs flex items-center gap-1 border border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => confirmAndUpdateAllForUser(uid, 'Rejected')}
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            Reject All
                          </button>
                        </div>
                      </div>
                      <div className="overflow-x-auto max-h-[350px] overflow-y-auto relative">
                        <table className="min-w-full text-sm">
                          <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#14161B] border-t border-[#2E2F33]' : 'bg-gray-100 border-t border-gray-200'}`}>
                            <tr className="h-10">
                              <th className="px-4 py-2 text-left font-semibold">Type</th>
                              <th className="px-4 py-2 text-left font-semibold">Reason</th>
                              <th className="px-4 py-2 text-left font-semibold">From</th>
                              <th className="px-4 py-2 text-left font-semibold">To</th>
                              <th className="px-4 py-2 text-center font-semibold">Days</th>
                              {isAdmin && <th className="px-4 py-2 text-left font-semibold">Manager</th>}
                              <th className="px-4 py-2 text-left font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody className={`${isDark ? 'divide-y divide-[#2E2F33]' : 'divide-y divide-gray-200'}`}>
                            {items.map(e => (
                              <tr key={`${uid}-${e.id}`} className={`${isDark ? 'bg-[#1A1C1F] border-b border-[#2E2F33] hover:bg-[#2A3441]' : 'bg-white border-b border-gray-200 hover:bg-gray-50'}`}>
                                <td className="px-4 py-2">{e.leaveType}</td>
                                <td className="px-4 py-2 truncate max-w-xs">{e.reason}</td>
                                <td className="px-4 py-2">{formatDateDDMMYYYY(e.fromDate)}</td>
                                <td className="px-4 py-2">{formatDateDDMMYYYY(e.toDate)}</td>
                                <td className="px-4 py-2 text-center" title={describeDayPortions(e) || undefined}>
                                  {e.numDays ? formatNumDays(e.numDays) : '-'}
                                </td>
                                {isAdmin && (
                                  <td className="px-4 py-2">
                                    <span className="text-gray-800">
                                      {Array.isArray(e.managerIds) && e.managerIds.length > 0
                                        ? e.managerIds.map((mid, index) => {
                                          const mu = users.find(u => u.id === mid)
                                          const mname = `${mu?.firstName || ''} ${mu?.lastName || ''}`.trim()
                                          return (
                                            <span key={mid}>
                                              {mname || <span className="text-red-500">Deleted Manager</span>}
                                              {index < (e.managerIds?.length || 0) - 1 && ', '}
                                            </span>
                                          )
                                        })
                                        : '-'}
                                    </span>
                                  </td>
                                )}
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    <button disabled={processingId === `${uid}-${e.id}`} className="relative px-2 py-1 bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C] rounded text-xs hover:bg-[#FAEDD0] disabled:opacity-50" onClick={() => updateStatus(uid, e.id, 'Approved')}>
                                      <span className={processingId === `${uid}-${e.id}` ? 'invisible' : ''}>Approve</span>
                                      {processingId === `${uid}-${e.id}` && <span className="absolute inset-0 flex items-center justify-center"><svg className="animate-spin h-3 w-3 text-[#B59143]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></span>}
                                    </button>
                                    <button disabled={processingId === `${uid}-${e.id}`} className="relative px-2 py-1 border border-red-300 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100 disabled:opacity-50" onClick={() => updateStatus(uid, e.id, 'Rejected')}>
                                      <span className={processingId === `${uid}-${e.id}` ? 'invisible' : ''}>Reject</span>
                                      {processingId === `${uid}-${e.id}` && <span className="absolute inset-0 flex items-center justify-center"><svg className="animate-spin h-3 w-3 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></span>}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto px-6 pb-2">
                  <table className="min-w-full text-sm">
                    <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#14161B] border-t border-[#2E2F33]' : 'bg-gray-100 border-t border-gray-200'}`}>
                      <tr className="h-10">
                        <th className="px-4 py-2 text-left font-semibold">Type</th>
                        <th className="px-4 py-2 text-left font-semibold">Reason</th>
                        <th className="px-4 py-2 text-left font-semibold">From</th>
                        <th className="px-4 py-2 text-left font-semibold">To</th>
                        <th className="px-4 py-2 text-center font-semibold">Days</th>
                        {isAdmin && <th className="px-4 py-2 text-left font-semibold">Manager</th>}
                        <th className="px-4 py-2 text-left font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'divide-y divide-[#2E2F33]' : 'divide-y divide-gray-200'}`}>
                      {displayed.map((e) => (
                        <tr key={`${selectedUser}-${e.id}`} className={`${isDark ? 'bg-[#1A1C1F] border-b border-[#2E2F33] hover:bg-[#2A3441]' : 'bg-white border-b border-gray-200 hover:bg-gray-50'}`}>
                          <td className="px-4 py-2">{e.leaveType}</td>
                          <td className="px-4 py-2 truncate max-w-xs">{e.reason}</td>
                          <td className="px-4 py-2">{formatDateDDMMYYYY(e.fromDate)}</td>
                          <td className="px-4 py-2">{formatDateDDMMYYYY(e.toDate)}</td>
                          <td className="px-4 py-2 text-center" title={describeDayPortions(e) || undefined}>
                                  {e.numDays ? formatNumDays(e.numDays) : '-'}
                                </td>
                          {isAdmin && (
                            <td className="px-4 py-2">
                              <span className="text-gray-800">
                                {Array.isArray(e.managerIds) && e.managerIds.length > 0
                                  ? e.managerIds.map((mid, index) => {
                                    const mu = users.find(u => u.id === mid)
                                    const name = `${mu?.firstName || ''} ${mu?.lastName || ''}`.trim()
                                    return (
                                      <span key={mid}>
                                        {name || <span className="text-red-500">Deleted User</span>}
                                        {index < (e.managerIds?.length || 0) - 1 && ', '}
                                      </span>
                                    )
                                  })
                                  : '-'}
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <button disabled={processingId === `${selectedUser}-${e.id}`} className="relative px-2 py-1 bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C] rounded text-xs hover:bg-[#FAEDD0] disabled:opacity-50" onClick={() => updateStatus(selectedUser, e.id, 'Approved')}>
                                <span className={processingId === `${selectedUser}-${e.id}` ? 'invisible' : ''}>Approve</span>
                                {processingId === `${selectedUser}-${e.id}` && <span className="absolute inset-0 flex items-center justify-center"><svg className="animate-spin h-3 w-3 text-[#B59143]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></span>}
                              </button>
                              <button disabled={processingId === `${selectedUser}-${e.id}`} className="relative px-2 py-1 border border-red-300 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100 disabled:opacity-50" onClick={() => updateStatus(selectedUser, e.id, 'Rejected')}>
                                <span className={processingId === `${selectedUser}-${e.id}` ? 'invisible' : ''}>Reject</span>
                                {processingId === `${selectedUser}-${e.id}` && <span className="absolute inset-0 flex items-center justify-center"><svg className="animate-spin h-3 w-3 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></span>}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            </div>
          </>
        )}
      </div>

      {confirmState?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmState(null)} />
          <div className="relative bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-md mx-4 p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{confirmState.title}</h3>
            {confirmState.description && (
              <p className="text-sm text-gray-600 mb-4">{confirmState.description}</p>
            )}
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100" onClick={() => setConfirmState(null)}>
                {confirmState.cancelText || 'Cancel'}
              </button>
              <button
                className="px-3 py-1.5 rounded-md border border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]"
                onClick={async () => { const fn = confirmState.onConfirm; setConfirmState(null); await fn(); }}
              >
                {confirmState.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
